/**
 * Idempotency Key Utility — the SINGLE source of idempotency-key generation
 * for the whole TypeScript workspace (client apps + core services). Every
 * layer that mints an idempotency key MUST go through one of these two
 * functions; do not hand-roll keys (no ad-hoc `createHash`, template strings,
 * or per-call randomness scattered across services).
 *
 * Two patterns, one module — pick by who owns the "attempt" boundary:
 *
 *  1. INTERACTIVE / CLIENT payment intents → {@link generateIdempotencyKey}
 *     The key is RANDOM, minted ONCE per intent (a user "Place order" /
 *     "Refund" action), then HELD and reused across transport retries of that
 *     intent. A genuinely new user action mints a fresh key. This is correct
 *     for interactive flows because the *user action* — not the inputs —
 *     bounds the attempt: a resubmit after a decline must be a NEW key (else
 *     the server replays the cached terminal `declined`). See
 *     `apps/checkout/.../CheckoutPage.tsx` and
 *     `apps/management/.../payment-row-actions.tsx`.
 *
 *  2. SERVER / BATCH idempotent operations → {@link deriveIdempotencyKey}
 *     The key is DETERMINISTICALLY derived from the inputs that uniquely
 *     identify the operation/attempt (so the inputs themselves carry the
 *     attempt identity — e.g. ACH vendor+bills+amount+date, recovery
 *     campaign+attempt-number). Same inputs → same key → natural dedupe with
 *     no state to hold. See `achPaymentService` and
 *     `customerPaymentRecoveryService`.
 *
 * Keys are valid for 24 hours on the API server. Length: 16-128 characters.
 *
 * @example
 * ```typescript
 * import { generateIdempotencyKey, deriveIdempotencyKey } from '@order-up/common';
 *
 * // (1) Interactive — mint once, hold across retries:
 * const key = generateIdempotencyKey('checkout', String(cartId));
 * // => "checkout-1234_1704123456789_a1b2c3d4-..."
 *
 * // (2) Server/batch — deterministic from inputs:
 * const key = deriveIdempotencyKey('recover', [paymentRecordId, attempt]);
 * // => "recover_pr-1_1"   (same inputs always produce this key)
 * ```
 */

/**
 * Generate idempotency key for payment requests
 *
 * Industry standard: Uses UUID v4 (RFC 4122) for cryptographically secure randomness
 * Format matches Stripe, PayPal, Square, and other major payment processors
 *
 * @param prefix - Operation prefix (e.g., 'checkout', 'refund', 'void', 'auth')
 * @param context - Optional context (e.g., orderId, retref) to make key more traceable
 * @returns Idempotency key in format: {prefix}[-{context}]_{timestamp}_{uuid}
 */
export function generateIdempotencyKey(prefix: string, context?: string): string {
  const timestamp = Date.now();

  // Use Web Crypto API for UUID v4 (industry standard)
  // Available in: Modern browsers, Node.js 19+, Cloudflare Workers, Deno
  let uuid: string;

  if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
    // Modern environment with Web Crypto API
    uuid = globalThis.crypto.randomUUID();
  } else if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    // Browser fallback
    uuid = window.crypto.randomUUID();
  } else {
    // 🔴 THE FALLBACK USED `Math.random()`, AND AN IDEMPOTENCY KEY IS NOT A
    // PLACE FOR PREDICTABLE RANDOMNESS. `Math.random()` is seeded per realm and
    // trivially predictable from a few outputs; a guessable key lets one
    // caller collide with another's in-flight request — which on this path
    // means suppressing or replaying a PAYMENT, since that is what idempotency
    // keys guard.
    //
    // It was dead code in practice (this repo requires Node 22, and every
    // browser that ships `crypto` has had `getRandomValues` for a decade). But
    // a security fallback nobody executes is exactly the one that goes live
    // unnoticed after an environment change, and CodeQL flagged it as
    // `js/insecure-randomness` for that reason.
    //
    // `getRandomValues` is available FAR more widely than `randomUUID` — it
    // predates it by years — so this fallback covers strictly more environments
    // than the one it replaces while being cryptographically sound.
    const rng = globalThis.crypto?.getRandomValues?.bind(globalThis.crypto);
    if (!rng) {
      // Refuse rather than degrade. An idempotency key that is not unique is
      // worse than a failed request: the request silently takes the identity
      // of another one.
      throw new Error(
        'No cryptographic random source is available, so a unique idempotency ' +
          'key cannot be generated. Refusing rather than emitting a guessable one.',
      );
    }
    const bytes = rng(new Uint8Array(16));
    // RFC 4122 §4.4: version 4, variant 10xx.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    uuid =
      `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
      `${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Include context if provided (e.g., orderId or retref)
  const contextPart = context ? `-${context}` : '';

  return `${prefix}${contextPart}_${timestamp}_${uuid}`;
}

/** Max idempotency-key length accepted by the payment API (see {@link isValidIdempotencyKey}). */
const MAX_IDEMPOTENCY_KEY_LENGTH = 128;

/** Normalize one key segment to a safe, stable token (alphanumerics, `.`, `-`). */
function normalizeIdempotencyPart(part: string | number): string {
  return String(part).trim().replace(/[^A-Za-z0-9.-]/g, '-');
}

/**
 * 128-bit FNV-1a over the UTF-8 bytes of `input` → 32 lowercase hex chars.
 * Deterministic and identical across every workspace runtime (browser, Node,
 * Workers) — TextEncoder and BigInt are universally available in our targets —
 * so a key derived on the client and one derived on the server from the same
 * inputs always match. Non-cryptographic by design: idempotency keys are
 * dedupe identifiers, not secrets.
 */
function fnv1a128Hex(input: string): string {
  const OFFSET = 0x6c62272e07bb014262b821756295c58dn;
  const PRIME = 0x0000000001000000000000000000013bn;
  const MASK = (1n << 128n) - 1n;
  let hash = OFFSET;
  const bytes = new TextEncoder().encode(input);
  for (let i = 0; i < bytes.length; i++) {
    hash ^= BigInt(bytes[i]);
    hash = (hash * PRIME) & MASK;
  }
  return hash.toString(16).padStart(32, '0');
}

/**
 * Deterministically derive a STABLE idempotency key from a scope and the
 * inputs that uniquely identify an operation/attempt. Same inputs → same key,
 * so a retried request is naturally deduped by the server's idempotency layer
 * with no state to hold.
 *
 * Use this for SERVER / BATCH idempotency where the attempt identity lives in
 * the inputs (ACH vendor+bills+amount+date, recovery campaign+attempt-number).
 * For INTERACTIVE client payment intents use {@link generateIdempotencyKey}
 * minted once and held — there the user action, not the inputs, bounds the
 * attempt (a resubmit after a decline must be a new key).
 *
 * Short input sets produce a readable composite (`{scope}_{a}_{b}`) for easy
 * tracing; sets that would exceed the 128-char API limit collapse to a stable
 * hash (`{scope}_{32-hex}`). Environment-agnostic + synchronous.
 *
 * @param scope - Operation scope (e.g. 'recover', 'ach', 'checkout')
 * @param parts - Inputs that uniquely identify the operation/attempt
 */
export function deriveIdempotencyKey(scope: string, parts: Array<string | number>): string {
  const composite = `${scope}_${parts.map(normalizeIdempotencyPart).join('_')}`;
  if (composite.length <= MAX_IDEMPOTENCY_KEY_LENGTH) {
    return composite;
  }
  return `${scope}_${fnv1a128Hex(parts.map((p) => String(p)).join('|'))}`;
}

/**
 * Validate idempotency key format
 * Must be between 16 and 128 characters per API requirements
 *
 * @param key - Idempotency key to validate
 * @returns true if valid, false otherwise
 */
export function isValidIdempotencyKey(key: string): boolean {
  return typeof key === 'string' && key.length >= 16 && key.length <= 128;
}

/**
 * Extract prefix from idempotency key
 *
 * @param key - Idempotency key
 * @returns prefix (e.g., 'checkout', 'refund') or null if invalid format
 */
export function extractIdempotencyPrefix(key: string): string | null {
  try {
    const parts = key.split('_');
    return parts.length >= 3 ? parts[0] : null;
  } catch {
    return null;
  }
}

/**
 * Extract timestamp from idempotency key
 *
 * @param key - Idempotency key
 * @returns Date object or null if invalid format
 */
export function extractIdempotencyTimestamp(key: string): Date | null {
  try {
    const parts = key.split('_');
    if (parts.length < 3) return null;

    const timestamp = parseInt(parts[1], 10);
    if (isNaN(timestamp)) return null;

    return new Date(timestamp);
  } catch {
    return null;
  }
}
